import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  normalizeIsraeliPhone,
  ISRAELI_PHONE_ERROR,
} from "../customer-validation";
import {
  buildBusinessContactLinks,
  type BusinessContactSettings,
} from "./business-contact";
import {
  BusinessQuoteSubmissionError,
  createBrowserBusinessQuoteApi,
} from "./business-quote-api";
import { Icon } from "./homepage-icons";

export function BusinessQuoteForm({
  businessSettings,
  requestedProduct,
  variant = "quote",
}: {
  businessSettings: BusinessContactSettings;
  requestedProduct: { name: string; revision: number } | null;
  variant?: "quote" | "contact";
}) {
  const detailsRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [details, setDetails] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const links = buildBusinessContactLinks(businessSettings);
  const quoteApi = createBrowserBusinessQuoteApi();
  useEffect(() => {
    if (!requestedProduct) return;
    setDetails(`אשמח להצעת מחיר עבור ${requestedProduct.name}.`);
    document.getElementById("business-quote")?.scrollIntoView?.({
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "center",
    });
    detailsRef.current?.focus({ preventScroll: true });
  }, [requestedProduct]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = String(data.get("name") ?? "").trim();
    const organization = String(data.get("organization") ?? "").trim();
    const phone = normalizeIsraeliPhone(String(data.get("phone") ?? ""));
    const nextErrors: Record<string, string> = {};
    if (!name) nextErrors.name = "יש להזין שם מלא.";
    if (!phone) nextErrors.phone = ISRAELI_PHONE_ERROR;
    if (!details.trim()) nextErrors.details = "ספרו לנו מה תרצו להדפיס.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      formRef.current
        ?.querySelector<HTMLElement>(`[name="${Object.keys(nextErrors)[0]}"]`)
        ?.focus();
      return;
    }
    setSubmitting(true);
    setSubmitted(false);
    setSubmitError(null);
    try {
      await quoteApi.submit({
        name,
        organization: organization || undefined,
        phone: phone ?? "",
        details: details.trim(),
        requestedProduct: requestedProduct?.name,
        website: String(data.get("website") ?? ""),
      });
      setSubmitted(true);
      setDetails("");
      formRef.current?.reset();
    } catch (error) {
      if (error instanceof BusinessQuoteSubmissionError && error.field) {
        setErrors(current => ({ ...current, [error.field!]: error.message }));
      }
      setSubmitError(
        error instanceof BusinessQuoteSubmissionError
          ? error.message
          : "לא הצלחנו לקבל את הבקשה כרגע. אפשר לנסות שוב או ליצור איתנו קשר ישירות.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const isContact = variant === "contact";

  return (
    <section
      className={`hp-quote-strip${isContact ? " hp-quote-strip--contact" : ""}`}
      id="business-quote"
      aria-labelledby="hp-quote-title"
    >
      <div className="hp-section hp-business-inner">
        <div className="hp-business-copy">
          <Icon name={isContact ? "service" : "business"} size={32} />
          <h2 id="hp-quote-title">
            {isContact ? "איך אפשר לעזור?" : "הצעת מחיר לעסקים ולמוסדות"}
          </h2>
          {isContact ? (
            <>
              <p>ספרו לנו מה תרצו להדפיס, למתג או לייצר.</p>
              <p>נחזור אליכם עם הכוונה לגבי המוצר, המידה, הכמות והמשך התהליך.</p>
            </>
          ) : (
            <>
              <p>לעסקים, בתי ספר, עיריות ומפיקי אירועים.</p>
              <p>ספרו לנו מה צריך להדפיס ונעזור להתאים את המוצר, המידה והכמות.</p>
            </>
          )}
        </div>
        <form
          onSubmit={event => void submit(event)}
          ref={formRef}
          noValidate
          className="hp-business-form"
          aria-label="בקשת הצעת מחיר"
        >
          <div className="hp-business-fields">
            {[
              {
                name: "name",
                label: "שם מלא",
                type: "text",
                autoComplete: "name",
              },
              {
                name: "organization",
                label: "שם העסק / המוסד",
                type: "text",
                autoComplete: "organization",
              },
              {
                name: "phone",
                label: "טלפון",
                type: "tel",
                autoComplete: "tel",
              },
            ].map((field) => (
              <label className="hp-field" key={field.name}>
                <span id={`quote-${field.name}-label`}>
                  {field.label}
                  {field.name === "organization" ? " (לא חובה)" : ""}
                </span>
                <input
                  name={field.name}
                  type={field.type}
                  autoComplete={field.autoComplete}
                  maxLength={field.name === "phone" ? 30 : 120}
                  required={field.name !== "organization"}
                  aria-labelledby={`quote-${field.name}-label`}
                  aria-invalid={Boolean(errors[field.name])}
                  aria-describedby={
                    errors[field.name] ? `quote-${field.name}-error` : undefined
                  }
                />
                {errors[field.name] ? (
                  <small
                    className="hp-field-error"
                    id={`quote-${field.name}-error`}
                  >
                    {errors[field.name]}
                  </small>
                ) : null}
              </label>
            ))}
          </div>
          <label className="hp-field">
            <span id="quote-details-label">מה צריך להדפיס?</span>
            <textarea
              name="details"
              ref={detailsRef}
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              rows={2}
              maxLength={1500}
              required
              aria-labelledby="quote-details-label"
              aria-invalid={Boolean(errors.details)}
              aria-describedby={
                errors.details ? "quote-details-error" : undefined
              }
              placeholder="המוצר, הכמות ומתי צריך אותו"
            />
            {errors.details ? (
              <small className="hp-field-error" id="quote-details-error">
                {errors.details}
              </small>
            ) : null}
          </label>
          <label className="hp-quote-honeypot" aria-hidden="true">
            אתר
            <input name="website" type="text" tabIndex={-1} autoComplete="off" />
          </label>
          {submitted ? (
            <p className="hp-business-success" role="status">
              הבקשה התקבלה בהצלחה. נחזור אליכם לפי פרטי הקשר שמילאתם.
            </p>
          ) : null}
          {submitError ? (
            <p className="hp-field-error" role="alert">{submitError}</p>
          ) : null}
          <div className="hp-business-submit">
            <button
              className="hp-btn hp-btn-business"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "שולחים בקשה…" : "שליחת בקשה"} <Icon name="mail" size={18} />
            </button>
            <small>
              הבקשה נשלחת ישירות לבית הדפוס. אין צורך לפתוח תוכנת מייל.
              {submitError && links.phoneHref ? " אפשר גם ליצור קשר טלפוני." : ""}
            </small>
          </div>
        </form>
      </div>
    </section>
  );
}
